import { QIcon, QTooltip, QMenu, QInput, QList, QItem, QItemSection, QItemLabel, QSeparator } from 'quasar';
import {
    USE_VARIABLE_TOOLTIP,
    USING_VARIABLE_TOOLTIP,
    ASSIGN_VARIABLE_TOOLTIP,
    EXPRESSION_CHIP_TOOLTIP,
    buildVariablePickerOptions,
} from './variableBinding.js';

export { buildVariablePickerOptions };

/** Icon used for variable affordance, picker, and bound pill. */
export const VARIABLE_BINDING_ICON = 'mdi-variable-box';

/** Icon for Essentials expression chip (non-representable Advanced value). */
export const EXPRESSION_CHIP_ICON = 'mdi-function-variant';
/**
 * Shared Option A chrome: assist icon + dedicated picker menu + bound variable pill.
 * Picking/creating a variable is a separate act from editing a literal field value.
 * Output / ReturnVar uses assign CTA + bare-name pill (AllowVariableTarget).
 */

/**
 * Dedicated pick/create menu (not a field swap). Place as child of the click target.
 */
export function renderVariablePickerMenu({
    modelValue,
    onUpdateOpen,
    filter,
    onUpdateFilter,
    options,
    onPick,
    onCreate,
    expectedType,
    mode,
}) {
    const typed = (filter || '').trim();
    const canCreate = !!(typed && options.some(o => o.create && o.value === typed && o.compatible !== false));
    const showExpected = !!(expectedType && String(expectedType).trim())
        && !(mode === 'use' && (!expectedType || String(expectedType).trim().toLowerCase() === 'string'));
    const pickable = options.filter(o => o.create || o.compatible !== false);
    const blocked = options.filter(o => !o.create && o.compatible === false);
    const ordered = pickable.concat(blocked);

    return (
        <QMenu
            modelValue={modelValue}
            onUpdate:modelValue={onUpdateOpen}
            anchor="bottom left"
            self="top left"
            no-parent-event
            class="c-variable-binding-picker"
            style={{ maxWidth: '280px' }}
        >
            <div class="q-pa-sm" style={{ minWidth: '240px' }} onMousedown={(e) => e.stopPropagation()}>
                <div style={{ fontSize: '11px', color: '#607d8b', marginBottom: '6px' }}>
                    {showExpected
                        ? `Expected type: ${String(expectedType).trim()}`
                        : 'Choose an existing variable or type a new name'}
                </div>
                <QInput
                    dense
                    outlined
                    autofocus
                    modelValue={filter}
                    onUpdate:modelValue={onUpdateFilter}
                    placeholder="Variable name…"
                    class="q-mb-xs"
                    onKeydown={(e) => {
                        if (e.key !== 'Enter')
                            return;
                        e.preventDefault();
                        e.stopPropagation();
                        if (!typed)
                            return;
                        if (canCreate)
                            onCreate?.(typed);
                        else {
                            const hit = options.find(o => !o.create && o.value === typed && o.compatible !== false);
                            if (hit)
                                onPick?.(typed, { create: false });
                        }
                    }}
                >
                    {{
                        prepend: () => <QIcon name="mdi-variable-box" size="xs" color="primary" />,
                    }}
                </QInput>
                <QSeparator class="q-mb-xs" />
                <QList dense style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {ordered.length === 0 ? (
                        <QItem dense>
                            <QItemSection>
                                <QItemLabel caption>
                                    {typed ? 'Press Enter to create' : 'No variables yet — type a name above'}
                                </QItemLabel>
                            </QItemSection>
                        </QItem>
                    ) : (
                        ordered.map((opt, idx) => {
                            const disabled = !opt.create && opt.compatible === false;
                            const showSep = blocked.length > 0
                                && idx === pickable.length
                                && pickable.length > 0;
                            const caption = opt.create
                                ? 'Add to Variables list'
                                : disabled
                                    ? `${opt.type || '?'} — type mismatch`
                                    : opt.kind === 'parameter'
                                        ? `${opt.type || 'parameter'} · Parameter`
                                        : (opt.type ? `${opt.type} · Local` : 'Existing variable');
                            const rowKey = `${opt.create ? 'c:' : 'v:'}${opt.kind || 'l'}:${opt.value}`;
                            return (
                                <div key={rowKey}>
                                    {showSep ? <QSeparator class="q-my-xs" /> : null}
                                    <QItem
                                        clickable={!disabled}
                                        dense
                                        disable={disabled}
                                        style={disabled ? { opacity: 0.55 } : undefined}
                                        onClick={() => {
                                            if (disabled)
                                                return;
                                            if (opt.create)
                                                onCreate?.(opt.value);
                                            else
                                                onPick?.(opt.value, { create: false });
                                        }}
                                    >
                                        <QItemSection avatar style={{ minWidth: '28px' }}>
                                            <QIcon
                                                name={opt.create
                                                    ? 'mdi-plus-circle-outline'
                                                    : opt.kind === 'parameter'
                                                        ? 'mdi-function-variant'
                                                        : 'mdi-variable-box'}
                                                size="xs"
                                                color={opt.create ? 'positive' : (disabled ? 'grey' : 'primary')}
                                            />
                                        </QItemSection>
                                        <QItemSection>
                                            <QItemLabel>{opt.create ? opt.label : opt.value}</QItemLabel>
                                            <QItemLabel caption>{caption}</QItemLabel>
                                        </QItemSection>
                                    </QItem>
                                </div>
                            );
                        })
                    )}
                </QList>
            </div>
        </QMenu>
    );
}

/**
 * Light-blue chip that fully replaces the literal control when a variable is bound.
 * Optional `menu` is typically the picker QMenu anchored to this pill.
 */
export function renderVariablePill({ name, label, onClear, onChange, disable, menu, clearTooltip }) {
    const tip = clearTooltip || USING_VARIABLE_TOOLTIP;
    return (
        <div
            class="c-variable-binding-pill-wrap"
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                // Match Quasar dense outlined field bottom-slots spacing so the next field is not flush
                marginBottom: '20px',
            }}
        >
            {label ? (
                <div style={{ fontSize: '11px', color: '#607d8b', lineHeight: 1.2 }}>{label}</div>
            ) : null}
            <div
                class="c-variable-binding-pill"
                role="button"
                tabindex={disable ? -1 : 0}
                title="Click to change variable"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 10px',
                    borderRadius: '16px',
                    background: 'rgba(25, 103, 210, 0.12)',
                    border: '1px solid rgba(25, 103, 210, 0.45)',
                    color: '#1967D2',
                    maxWidth: '100%',
                    cursor: disable ? 'default' : 'pointer',
                    boxSizing: 'border-box',
                    alignSelf: 'flex-start',
                    minHeight: '32px',
                    position: 'relative',
                }}
                onClick={(e) => {
                    if (disable)
                        return;
                    e.preventDefault();
                    e.stopPropagation();
                    onChange?.(e);
                }}
                onKeydown={(e) => {
                    if (disable)
                        return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onChange?.(e);
                    }
                }}
            >
                <QIcon name="mdi-variable-box" size="xs" style={{ flex: '0 0 auto' }} />
                <span
                    style={{
                        fontWeight: 600,
                        fontSize: '13px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {name}
                </span>
                <QIcon
                    name="mdi-close"
                    size="xs"
                    class="c-variable-binding-pill-clear"
                    style={{ flex: '0 0 auto', opacity: 0.85 }}
                    title={tip}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!disable)
                            onClear?.(e);
                    }}
                >
                    <QTooltip
                        anchor="top middle"
                        self="bottom middle"
                        transition-show="scale"
                        transition-hide="scale"
                    >
                        {tip}
                    </QTooltip>
                </QIcon>
                {menu}
            </div>
        </div>
    );
}

/**
 * Empty-state CTA for Output / ReturnVar (no free-text input). Opens the same pick/create menu.
 */
export function renderVariableAssignCta({ label, onOpenPicker, disable, menu, tooltip }) {
    const tip = tooltip || ASSIGN_VARIABLE_TOOLTIP;
    return (
        <div
            class="c-variable-assign-cta-wrap"
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                marginBottom: '20px',
            }}
        >
            {label ? (
                <div style={{ fontSize: '11px', color: '#607d8b', lineHeight: 1.2 }}>{label}</div>
            ) : null}
            <div
                class="c-variable-assign-cta"
                role="button"
                tabindex={disable ? -1 : 0}
                title={tip}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 12px',
                    borderRadius: '8px',
                    background: 'transparent',
                    border: '1px dashed rgba(25, 103, 210, 0.55)',
                    color: '#1967D2',
                    maxWidth: '100%',
                    cursor: disable ? 'default' : 'pointer',
                    boxSizing: 'border-box',
                    alignSelf: 'stretch',
                    minHeight: '36px',
                    position: 'relative',
                }}
                onClick={(e) => {
                    if (disable)
                        return;
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenPicker?.(e);
                }}
                onKeydown={(e) => {
                    if (disable)
                        return;
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpenPicker?.(e);
                    }
                }}
            >
                <QIcon name={VARIABLE_BINDING_ICON} size="xs" style={{ flex: '0 0 auto' }} />
                <span style={{ fontWeight: 600, fontSize: '13px' }}>
                    Assign variable…
                </span>
                <QTooltip
                    anchor="top middle"
                    self="bottom middle"
                    transition-show="scale"
                    transition-hide="scale"
                >
                    {tip}
                </QTooltip>
                {menu}
            </div>
        </div>
    );
}

/**
 * Amber chip replacing a typed Essentials control when the model holds an expression
 * that cannot be shown as a literal. Clear-only — after clear, Use Variable / Advanced
 * work again on the normal field.
 */
export function renderExpressionChip({ expression, label, onClear, disable }) {
    const text = expression == null ? '' : String(expression);
    return (
        <div
            class="c-essentials-expression-chip-wrap"
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                marginBottom: '20px',
            }}
        >
            {label ? (
                <div style={{ fontSize: '11px', color: '#607d8b', lineHeight: 1.2 }}>{label}</div>
            ) : null}
            <div
                class="c-essentials-expression-chip"
                title={EXPRESSION_CHIP_TOOLTIP}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 10px',
                    borderRadius: '16px',
                    background: 'rgba(245, 124, 0, 0.12)',
                    border: '1px solid rgba(245, 124, 0, 0.45)',
                    color: '#E65100',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    alignSelf: 'flex-start',
                    minHeight: '32px',
                    cursor: 'default',
                }}
            >
                <QIcon name={EXPRESSION_CHIP_ICON} size="xs" style={{ flex: '0 0 auto' }} />
                <span
                    style={{
                        fontWeight: 600,
                        fontSize: '13px',
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {text}
                </span>
                <QIcon
                    name="mdi-close"
                    size="xs"
                    class="c-essentials-expression-chip-clear"
                    style={{ flex: '0 0 auto', opacity: 0.85, cursor: disable ? 'default' : 'pointer' }}
                    title={EXPRESSION_CHIP_TOOLTIP}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!disable)
                            onClear?.(e);
                    }}
                >
                    <QTooltip
                        anchor="top middle"
                        self="bottom middle"
                        transition-show="scale"
                        transition-hide="scale"
                    >
                        {EXPRESSION_CHIP_TOOLTIP}
                    </QTooltip>
                </QIcon>
            </div>
        </div>
    );
}

/**
 * Hover/focus append affordance that opens the picker (does not mutate the field value).
 */
export function renderVariableAssistIcon({
    visible,
    pickerOpen,
    onOpenPicker,
    menu,
    tooltip,
}) {
    if (!visible && !pickerOpen)
        return null;

    const tip = tooltip || USE_VARIABLE_TOOLTIP;

    return (
        <QIcon
            name="mdi-variable-box"
            size="xs"
            class="c-variable-binding-assist"
            style={{
                cursor: 'pointer',
                opacity: visible || pickerOpen ? 1 : 0,
                color: pickerOpen ? '#1967D2' : '#607d8b',
                transition: 'opacity 0.12s ease',
            }}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onOpenPicker?.(e);
            }}
            onMousedown={(e) => e.preventDefault()}
        >
            <QTooltip
                anchor="top middle"
                self="bottom middle"
                transition-show="scale"
                transition-hide="scale"
            >
                {tip}
            </QTooltip>
            {menu}
        </QIcon>
    );
}
